import './about.css';
import { useEffect } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import SiteFooter from "../components/SiteFooter";

const AffiliationLink = ({ href, children }) => (
  <a className="AboutAffiliationLink" href={href} target="_blank" rel="noreferrer">
    {children} <FiArrowUpRight aria-hidden="true" />
  </a>
);

function About() {

  useEffect(() => {
    document.title = "About | Jérôme Lecoq";
  }, []);

  return (
    <main className="AboutPage">
      <section className="AboutHero">
        <h1>
          I build open, large-scale neuroscience platforms to tackle questions about the
          brain that no single lab could answer alone.
        </h1>
        <div className="AboutIntroduction">
          <img src="/jerome-lecoq.jpg" alt="Jérôme Lecoq" />
          <div>
            <p className="AboutLead">
              My work combines experimental neuroscience, engineering, quantitative
              analysis, and AI to study how neural populations transform sensation and
              experience into perception, memory, and action.
            </p>
            <p>
              At the Allen Institute, I helped establish the Allen Brain Observatory, then
              co-established OpenScope, secured its funding, and continue to lead it as a
              platform where researchers can propose neurophysiology experiments.
            </p>
            <div className="AboutLinks">
              <a href="https://alleninstitute.org/person/jerome-lecoq/" target="_blank" rel="noreferrer">
                Allen Institute <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="https://www.linkedin.com/in/lecoqjerome/?locale=en" target="_blank" rel="noreferrer">
                LinkedIn <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="https://scholar.google.com/citations?user=qEadPMEAAAAJ&hl=en" target="_blank" rel="noreferrer">
                All papers <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="/academic-cv.pdf" target="_blank" rel="noreferrer">
                Academic CV <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="https://orcid.org/0000-0002-0131-0938" target="_blank" rel="noreferrer">
                ORCID <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="https://neurotree.org/neurotree/peopleinfo.php?pid=82399" target="_blank" rel="noreferrer">
                Neurotree <FiArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="AboutSection" aria-labelledby="path-heading">
        <h2 id="path-heading">Path</h2>
        <div className="AboutTimeline">
          <article>
            <span>2015–present</span>
            <div>
              <h3>
                <AffiliationLink href="https://alleninstitute.org/">
                  Allen Institute
                </AffiliationLink>
              </h3>
              <p>
                I joined the Allen Institute in 2015 under Christof Koch&apos;s leadership. There,
                I co-led the development of the field&apos;s first brain observatory built on
                calcium imaging. We developed a rigorous pipeline for complex systems neuroscience
                experiments, spanning standardized experimental design, high-throughput in vivo
                two-photon imaging, quality control, and public data release. The resulting
                Allen Brain Observatory produced numerous reusable datasets for the community. I then
                co-established OpenScope, secured its funding, and continue to lead the program,
                opening this infrastructure to experiments designed by researchers worldwide.
              </p>
            </div>
          </article>
          <article className="AboutParallelPeriod">
            <span>2010–2015</span>
            <div className="AboutParallelRoles">
              <section>
                <small>Postdoctoral research</small>
                <h3>
                  <AffiliationLink href="https://www.stanford.edu/">
                    Stanford University
                  </AffiliationLink>
                </h3>
                <p>
                  In <a
                    className="AboutInlineAffiliation"
                    href="https://schnitzerlab.stanford.edu/"
                    target="_blank"
                    rel="noreferrer"
                  >Mark Schnitzer&apos;s group</a>, my work combined instrument development,
                  experimentation, software, and quantitative analysis. I co-developed multiple
                  one- and two-photon microscopes for simultaneously recording neural activity from
                  distant cortical areas in behaving mice and contributed to methods for chronic
                  optical access to more than 30 cortical areas. Using these large-scale recordings,
                  we studied sensory coding, communication between cortical areas, and skilled movement.
                </p>
              </section>
              <section>
                <small>Commercial translation</small>
                <h3>
                  <AffiliationLink href="https://www.inscopix.com/">
                    Inscopix
                  </AffiliationLink>
                </h3>
                <p>
                  In parallel with my postdoctoral research, I contributed to analysis
                    tools for miniature-microscope calcium imaging that were commercialized for use
                    in academic laboratories. My work focused on the workflow from raw recordings through
                    preprocessing, motion correction, cell and
                  trace extraction, event detection, and export.
                </p>
              </section>
            </div>
          </article>
          <article>
            <span>2004–2009</span>
            <div>
              <h3>
                <AffiliationLink href="https://www.institut-vision.org/en/research/imaging-sensory-information-processing-and-neurovascular-coupling">
                  Serge Charpak&apos;s laboratory, Paris
                </AffiliationLink>
              </h3>
              <p>
                For my Ph.D. in Neuroscience at <a
                  className="AboutInlineAffiliation"
                  href="https://www.sorbonne-universite.fr/en"
                  target="_blank"
                  rel="noreferrer"
                >Pierre and Marie Curie University</a>, I
                studied neurovascular coupling and brain metabolism with Serge Charpak. We connected
                neuronal activity to local blood flow and oxygen consumption. I combined oxygen
                electrode measurements with two-photon imaging of calcium and blood flow. I also
                developed a novel two-photon phosphorescence lifetime microscopy method to measure
                oxygen in vivo.
              </p>
            </div>
          </article>
          <article>
            <span>2003</span>
            <div>
              <h3>
                <AffiliationLink href="https://csl.sony.fr/">
                  Sony Computer Science Laboratories, Paris
                </AffiliationLink>
              </h3>
              <p>
                I was introduced to artificial intelligence early in my career through a formative
                experience at Sony Computer Science Laboratories in Paris. I have continued to develop
                this expertise throughout my career, applying AI and machine learning
                to experimental design, data analysis, and scientific workflows.
              </p>
            </div>
          </article>
          <article>
            <span>2000–2004</span>
            <div>
              <h3>
                <AffiliationLink href="https://psl.eu/en/espci-paris-psl">
                  ESPCI Paris
                </AffiliationLink>
              </h3>
              <p>
                I trained as an engineer across physics, chemistry, biology, mathematics,
                and numerical methods at ESPCI ParisTech. A large share of that education
                was practical experimental work: moving from hypotheses and experimental
                design through laboratory measurement, modeling, and analysis. That habit of
                joining theory with hands-on investigation still shapes how I approach
                biological questions and develop scientific instrumentation.
              </p>
            </div>
          </article>
          <article>
            <span>1998–2000</span>
            <div>
              <h3>
                <AffiliationLink href="https://www.onisep.fr/ressources/structures-enseignement/ile-de-france/paris/lycee-saint-louis">
                  Lycée Saint-Louis, Paris
                </AffiliationLink>
              </h3>
              <p>
                At Lycée Saint-Louis, I studied physics, mathematics, and chemistry through
                France&apos;s intensive classes préparatoires aux grandes écoles. This training
                built the quantitative foundation of my career.
              </p>
            </div>
          </article>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
export default About;